import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Alert, Form } from 'react-bootstrap'
import Modal from '@severed-links/common.modal'
import { HEROKU_VERSION } from '@severed-links/common.redherrings-constants'
import { getMe, getProfile } from '@severed-links/common.redherrings-reducers/account'
import { acceptLegalAgreement, getLegalAgreementStatus, resetLegalAgreement } from '@severed-links/common.redherrings-reducers/legalAgreements'
import * as s from './LegalAgreementWatcher.scss'

const AgreementContent = ({ content = '' }) => (
    <div className={s.agreementContent}>
        {content.split('\n\n').filter(i => i.trim()).map((block, index) => {
            const text = block.trim()
            if (text.startsWith('# ')) return <h2 key={index}>{text.slice(2)}</h2>
            if (text.startsWith('## ')) return <h3 key={index}>{text.slice(3)}</h3>
            return <p key={index}>{text}</p>
        })}
    </div>
)

const LegalAgreementWatcher = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const { pathname } = location
    const { _id: playerId, access_token, hasCheckedToken, isAuthenticated, isMyProfileLoaded, isMyExtendedProfileLoaded } = useSelector(state => state.account)
    const { agreement, acceptanceRequired, hasCheckedStatus, isFetching, isSaving, error } = useSelector(state => state.legalAgreements)
    const [hasAgreed, setHasAgreed] = useState(false)
    const isNotRootPath = pathname !== '/'
    const sessionIsReady = isNotRootPath && hasCheckedToken && isAuthenticated && !!access_token

    useEffect(() => {
        if (!sessionIsReady) {
            dispatch(resetLegalAgreement())
            setHasAgreed(false)
        }
    }, [sessionIsReady])

    useEffect(() => {
        if (sessionIsReady && !isMyProfileLoaded) dispatch(getMe())
    }, [sessionIsReady, isMyProfileLoaded])

    useEffect(() => {
        if (sessionIsReady && isMyProfileLoaded && !isMyExtendedProfileLoaded) dispatch(getProfile())
    }, [sessionIsReady, isMyProfileLoaded, isMyExtendedProfileLoaded])

    useEffect(() => {
        if (sessionIsReady && isMyProfileLoaded && playerId) dispatch(getLegalAgreementStatus())
    }, [sessionIsReady, isMyProfileLoaded, playerId])

    useEffect(() => setHasAgreed(false), [agreement?._id])

    const retry = () => dispatch(getLegalAgreementStatus())

    const accept = () => {
        if (!agreement) {
            retry()
            return
        }
        dispatch(acceptLegalAgreement(agreement._id, agreement.version, {
            source: 'Web',
            platform: 'Web',
            appVersion: HEROKU_VERSION,
        }))
    }

    const show = sessionIsReady && isMyProfileLoaded && !!playerId && hasCheckedStatus && acceptanceRequired
    const isBusy = isFetching || isSaving
    const canAccept = !!agreement && hasAgreed && !isBusy

    return (
        <Modal show={show} heading='Redherrings Terms of Use' size='lg'
            showCloseButton={false} backdrop='static' onClose={() => {}}
            actionButtonText={agreement ? 'Accept EULA' : 'Retry'}
            actionButtonIcon='check'
            actionButtonIconSpin={isBusy}
            actionButtonDisabled={agreement ? !canAccept : isBusy}
            actionButtonOnClick={agreement ? accept : retry}>
            {error ? <Alert variant='danger'>{error}</Alert> : null}
            {agreement ?
            <>
                <div className={s.version}>Version {agreement.version} · Effective {new Date(agreement.effectiveAt).toLocaleDateString()}</div>
                <AgreementContent content={agreement.content} />
                <Form.Check className={s.acceptanceCheck} type='checkbox'
                    id='accept-current-eula'
                    checked={hasAgreed}
                    onChange={e => setHasAgreed(e.target.checked)}
                    label='I have read and agree to the Redherrings EULA and Terms of Use.' />
            </>
            : null}
        </Modal>
    )
}

export default LegalAgreementWatcher
